import { useId } from 'react';

/** Props for {@link TextField}. */
export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'id'> {
  /** Visible label. Never omitted -- a placeholder is not a label. */
  readonly label: string;
  /** Validation message. Presence switches the field into its error state. */
  readonly error?: string;
  /** Guidance shown below the field. */
  readonly hint?: string;
}

/**
 * A labelled single-line text input.
 *
 * The label, hint and error are wired together with `id`/`aria-describedby` so assistive
 * technology announces the validation message with the field rather than stranding it.
 *
 * @param props - Component props.
 * @returns The field element.
 */
export function TextField({ label, error, hint, ...rest }: TextFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div>
      <label className="gm-field-label" htmlFor={id}>
        {label}
      </label>
      <input
        {...rest}
        id={id}
        className="gm-input"
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />
      {hint !== undefined && (
        <p id={hintId} className="gm-text-muted" style={{ margin: '4px 0 0', fontSize: '14px' }}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className="gm-field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
